import clsx from "clsx";
import type { GemStack } from "../types";
import { getGemIconPath, GEM_ICON_FALLBACK } from "../utils/assets";
import { AssetIcon } from "./AssetIcon";
import { ModalBackdrop } from "./ModalBackdrop";

const GEM_TYPES = ["PATK", "PDEF", "MATK", "MDEF", "CRIT", "HP"] as const;

interface GemPickerModalProps {
  gems: GemStack[];
  heroClass?: string;
  onSelect: (gemType: string, level: number) => void;
  onClose: () => void;
}

export function GemPickerModal({ gems, heroClass, onSelect, onClose }: GemPickerModalProps) {
  const usable = gems.filter((g) => g.quantity > 0 && GEM_TYPES.includes(g.gem_type as (typeof GEM_TYPES)[number]));

  return (
    <ModalBackdrop onClose={onClose} className="z-[60] items-end bg-black/70">
      <div className="w-full max-w-sm bg-kingdom-purple border border-kingdom-light rounded-2xl p-4 space-y-3 max-h-[70vh] overflow-y-auto">
        <div className="flex items-center justify-between">
          <h3 className="font-bold text-white">Insert gem</h3>
          <button type="button" onClick={onClose} className="text-gray-400 text-xl">
            ✕
          </button>
        </div>

        {usable.length === 0 && (
          <p className="text-gray-400 text-sm text-center py-4">No gems in inventory. Clear catacombs floors to earn gems.</p>
        )}

        <div className="grid grid-cols-2 gap-2">
          {usable.map((g) => (
            <button
              key={`${g.gem_type}-${g.level}`}
              type="button"
              onClick={() => onSelect(g.gem_type, g.level)}
              className="rounded-xl border border-kingdom-mid bg-kingdom-dark p-3 text-left hover:border-kingdom-glow active:scale-95"
            >
              <div className="flex items-center gap-2 mb-1">
                <AssetIcon
                  src={getGemIconPath(g.gem_type, g.level)}
                  fallback={GEM_ICON_FALLBACK}
                  alt={`${g.gem_type} Lv.${g.level}`}
                  className="w-8 h-8 object-contain shrink-0"
                  emojiClassName="text-xl leading-none"
                />
                <div className={clsx("font-bold text-sm", gemTypeColor(g.gem_type, heroClass))}>
                  {formatGemLabel(g.gem_type, heroClass)} Lv.{g.level}
                </div>
              </div>
              <div className="text-xs text-gray-400">×{g.quantity}</div>
              <GemStatPreview gem={g} heroClass={heroClass} />
            </button>
          ))}
        </div>
      </div>
    </ModalBackdrop>
  );
}

function GemStatPreview({ gem, heroClass }: { gem: GemStack; heroClass?: string }) {
  const key = gemStatKey(gem.gem_type, heroClass);
  const val = gem.stats[key] ?? gem.stats[gem.gem_type] ?? Object.values(gem.stats)[0];
  if (val == null) return null;
  const suffix = gem.gem_type === "CRIT" ? "%" : gem.gem_type === "HP" ? "" : "";
  return (
    <div className="text-[10px] text-emerald-400 mt-1">
      +{val}
      {suffix}
    </div>
  );
}

export function formatGemLabel(gemType: string, heroClass?: string): string {
  if (gemType === "LUCK") return "Luck Stone";
  if (gemType === "PATK" && heroClass === "MAGE") return "MATK";
  if (gemType === "PATK") return "PATK";
  return gemType;
}

export function gemTypeColor(gemType: string, heroClass?: string): string {
  if (gemType === "PATK" || gemType === "MATK") {
    if (heroClass === "MAGE") return "text-sky-400";
    return "text-rose-400";
  }
  if (gemType === "CRIT") return "text-amber-400";
  if (gemType === "HP") return "text-emerald-400";
  return "text-violet-300";
}

export function gemStatKey(gemType: string, heroClass?: string): string {
  if (gemType === "PATK" && heroClass === "MAGE") return "MATK";
  return gemType;
}

export function gemTypeSurface(gemType: string, heroClass?: string): string {
  if (gemType === "LUCK") return "bg-violet-950 border-violet-600";
  if (gemType === "CRIT") return "bg-amber-950 border-amber-600";
  if (gemType === "HP") return "bg-emerald-950 border-emerald-600";
  if (gemType === "PATK" || gemType === "MATK") {
    if (heroClass === "MAGE") return "bg-sky-950 border-sky-600";
    return "bg-rose-950 border-rose-600";
  }
  if (gemType === "PDEF" || gemType === "MDEF") return "bg-indigo-950 border-indigo-600";
  return "bg-violet-950 border-violet-600";
}
